import React, { useState, useEffect } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import "./speaker.css";
import SpeakerModal from "./SpeakerModal";
import { db } from "../firebaseConfig";

const SpeakersSection = () => {
  const [showAll, setShowAll] = useState(true);
  const [speakers, setSpeakers] = useState<any[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const placeholderImage =
    "https://res.cloudinary.com/astcdubai/image/upload/v1736906559/placeholder_male.jpg";

  const truncateText = (text: string, limit: number) => {
    if (!text) return "";
    return text.length > limit ? text.slice(0, limit) + "..." : text;
  };

  const openModal = (speaker: any) => {
    setSelectedSpeaker(speaker);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedSpeaker(null);
    setIsModalOpen(false);
  };

  // Fetch speakers (single effect, keep EMCEE last)
  useEffect(() => {
    const fetchSpeakers = async () => {
      const registrationsRef = collection(db, "speakers");
      try {
        const q = query(registrationsRef, orderBy("timestamp", "asc"));
        const querySnapshot = await getDocs(q);

        const speakersData: any[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const isEmcee = (role: any) =>
          String(role || "")
            .trim()
            .toLowerCase() === "emcee";

        // Split groups
        const maktoum = speakersData.filter((s) =>
          String(s.name || "")
            .toLowerCase()
            .includes("al maktoum")
        );

        const mohammed = speakersData.filter((s) =>
          String(s.name || "")
            .toLowerCase()
            .includes("al hashemi")
        );
        const emcees = speakersData.filter(
          (s) =>
            String(s.role || "")
              .trim()
              .toLowerCase() === "emcee"
        );
        const others = speakersData.filter(
          (s) =>
            !String(s.name || "")
              .toLowerCase()
              .includes("al hashemi") &&
            String(s.role || "")
              .trim()
              .toLowerCase() !== "emcee"
        );

        // Helper: strip academic titles for comparison
        const stripTitle = (name: string = "") =>
          name
            .replace(/^(dr\.|prof\.|h\.e\.|mr\.|mrs\.|ms\.)\s*/i, "") // remove common prefixes
            .trim();

        // Sort "others" alphabetically ignoring titles
        others.sort((a, b) =>
          stripTitle(a.name).localeCompare(stripTitle(b.name), undefined, {
            sensitivity: "base",
          })
        );

        // Final order: Mohammed → others → Emcees
        setSpeakers([...maktoum, ...mohammed, ...others, ...emcees]);
      } catch (error) {
        console.error("Error fetching speakers data:", error);
      }
    };

    fetchSpeakers();
  }, []);

  // Screen size watcher
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 800);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleSpeakers = showAll ? speakers : speakers.slice(0, 5);

  return (
    <section id="speakers" className="speakers pt-120 pb-80 brd-bottom">
      <div className="container">
        <div className="row">
          <div className="col-sm-8 col-sm-offset-2 mb-100 text-center">
            <h1 className="title">Speakers</h1>
            <p className="sub-title-3 mt-10">
              Hear from world-renowned experts and industry leaders shaping the
              future of financial markets
            </p>
          </div>
        </div>

        {/* Desktop / large */}
        {!isSmallScreen ? (
          <div className="row">
            <TransitionGroup className="row">
              {visibleSpeakers.map((speaker) => (
                <CSSTransition key={speaker.id} timeout={500} classNames="fade">
                  <div className="col-custom-5 pd-1">
                    <div
                      className="block-speaker"
                      role="button"
                      tabIndex={0}
                      onClick={() => openModal(speaker)}
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") &&
                        openModal(speaker)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <div className="block-img soft">
                        <div className="speaker-img">
                          <img
                            src={speaker.imageUrl || placeholderImage}
                            alt={speaker.name}
                          />
                          <div className="bio-container">
                            <div className="bio">
                              <p>{speaker.shortBio}</p>
                            </div>
                          </div>
                        </div>
                        <div className="block-info-2">
                          <p>
                            <strong>{speaker.name}</strong>
                            <span>{speaker.role}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CSSTransition>
              ))}
            </TransitionGroup>
          </div>
        ) : (
          // Mobile grid
          <div className="speakers-grid">
            {visibleSpeakers.map((speaker) => (
              <CSSTransition key={speaker.id} timeout={500} classNames="fade">
                <div className="speaker-card">
                  <div
                    className="block-speaker"
                    role="button"
                    tabIndex={0}
                    onClick={() => openModal(speaker)}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") && openModal(speaker)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="block-img soft">
                      <div className="speaker-img">
                        <img
                          src={speaker.imageUrl || placeholderImage}
                          alt={speaker.name}
                        />
                        <div className="bio-container">
                          <div className="bio">
                            <p>{speaker.shortBio}</p>
                          </div>
                        </div>
                      </div>
                      <div className="block-info-2">
                        <p>
                          <strong>{speaker.name}</strong>
                          <span>{speaker.role}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CSSTransition>
            ))}
          </div>
        )}
      </div>

      {speakers.length > 5 && (
        <div className="row text-center mt-20">
          <h3 className="sub-title-2">{!showAll ? "View all speakers" : ""}</h3>

          <div
            className={`btn-primary ${showAll ? "view-less" : ""}`}
            onClick={() => setShowAll(!showAll)}
          >
            <span className={`arrow ${showAll ? "up" : "down"}`}></span>
          </div>
          <h3 className="sub-title-2">{!showAll ? "" : "View less"}</h3>
        </div>
      )}

      {/* Speaker Modal */}
      <SpeakerModal
        isOpen={isModalOpen}
        speaker={selectedSpeaker}
        onClose={closeModal}
      />
    </section>
  );
};

export default SpeakersSection;
